import type { PointerEvent } from 'react';
import { useTranslation } from '../../i18n/useTranslation';
import { pointLabel, type GridCell } from '../../services/lightSurveyService';
import { FLOW_LEVELS, formatRange, type FlowStatus, type PumpOutlet, type SpotFlag } from '../../services/flowSurveyService';
import '../lighting/Lighting.css';
import './FlowMap.css';

/** An outlet towards the viewer is drawn like an arrow seen tip-on (⊙), away from the viewer tail-on (⊗). */
export const FACING_GLYPH: Readonly<Record<'front' | 'back', string>> = { front: '⊙', back: '⊗' };

export interface FlowMapPump {
  readonly id: string;
  readonly sequence: number;
  readonly x: number;
  readonly y: number;
  readonly outlets: readonly PumpOutlet[];
}

export interface FlowMapPoint {
  readonly id: string;
  readonly sequence: number;
  readonly x: number;
  readonly y: number;
  readonly levelMin: number;
  readonly levelMax: number;
  readonly alternating: boolean;
  readonly flag: SpotFlag;
}

export interface FlowMapCoral {
  readonly id: string;
  /** 1-based number, the same as in the coral list. */
  readonly number: number;
  readonly x: number;
  readonly y: number;
  readonly status: FlowStatus;
}

export interface FlowMapPhotoProps {
  imageUrl: string | null;
  /** Width / height of the photo — used for the frame when there is no photo. */
  aspect: number;
  pumps: readonly FlowMapPump[];
  points: readonly FlowMapPoint[];
  corals: readonly FlowMapCoral[];
  /** The level map to shade (peak or trough); cells hold the level in `par`. */
  grid: readonly GridCell[];
  showGrid: boolean;
  /** Pumps, readings, corals and the level map; false = the plain photo (the pending ring stays). */
  showMeasurements?: boolean;
  /** Readings to emphasise ("where can a new coral go?"); null = no emphasis. */
  highlightIds: ReadonlySet<string> | null;
  /** The position picked for the form (0–1), drawn as a ring. */
  pending: { x: number; y: number } | null;
  /** Tap / click on the photo → position for the form. Keyboard users type the position in the form. */
  onPick?: (x: number, y: number) => void;
}

const pct = (value: number): string => `${Math.max(0, Math.min(100, value * 100))}%`;
const round = (value: number): number => Math.round(Math.max(0, Math.min(1, value)) * 1000) / 1000;
const levelClass = (level: number): number => Math.max(0, Math.min(4, Math.round(level)));

const CORAL_GLYPH: Readonly<Record<FlowStatus, string>> = {
  ok: '✓',
  too_weak: '!',
  too_strong: '!',
  uncertain: '?',
  unknown_group: '?',
  no_data: '?',
};

/**
 * The survey photo with the flow overlay: the estimated level map (peak or
 * trough), the pumps with an arrow in their direction, the readings as ranges
 * ("A 1–3", "~" when the direction changes) and the placed corals. Decorative
 * (aria-hidden) like the light map: the lists below are the accessible UI, and
 * the position fields of the form are the keyboard way to place something.
 */
export function FlowMapPhoto({ imageUrl, aspect, pumps, points, corals, grid, showGrid, showMeasurements = true, highlightIds, pending, onPick }: FlowMapPhotoProps) {
  const { t } = useTranslation();
  const pick = (event: PointerEvent<HTMLDivElement>): void => {
    if (!onPick || event.button > 0) return;
    const rect = event.currentTarget.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return;
    onPick(round((event.clientX - rect.left) / rect.width), round((event.clientY - rect.top) / rect.height));
  };
  const frameClass = ['par-map__frame', onPick ? '' : 'par-map__frame--readonly', imageUrl ? '' : 'par-map__frame--no-photo'].filter(Boolean).join(' ');
  return (
    <figure className="par-map">
      <div className={frameClass} data-testid="flow-map-frame" onPointerUp={pick} style={imageUrl ? undefined : { aspectRatio: String(aspect) }}>
        {imageUrl && <img className="par-map__photo" src={imageUrl} alt={t('flow.map.photoAlt')} draggable={false} />}
        <div className="par-map__layer" aria-hidden="true">
          {showMeasurements &&
            showGrid &&
            grid.map((cell) => (
              <span
                key={`${cell.x}-${cell.y}`}
                className={`flow-map__cell flow-map__cell--${levelClass(cell.par)}`}
                data-testid="flow-map-cell"
                style={{ left: pct(cell.x), top: pct(cell.y), width: pct(cell.width), height: pct(cell.height) }}
              />
            ))}
          {showMeasurements &&
            pumps.map((p) => (
              <span key={p.id} className="flow-map__pump" data-testid="flow-map-pump" style={{ left: pct(p.x), top: pct(p.y) }}>
                {p.outlets.map((o) =>
                  typeof o === 'number' ? (
                    <span key={o} className="flow-map__pump-arrow" data-testid="flow-map-pump-arrow" style={{ transform: `translateY(-50%) rotate(${o}deg)` }}>
                      <span className="flow-map__pump-shaft" />
                      ▶
                    </span>
                  ) : (
                    <span key={o} className={`flow-map__pump-facing flow-map__pump-facing--${o}`} data-testid="flow-map-pump-facing">
                      {FACING_GLYPH[o]}
                    </span>
                  ),
                )}
                P{p.sequence}
              </span>
            ))}
          {showMeasurements &&
            points.map((p) => {
              const emphasis = highlightIds ? (highlightIds.has(p.id) ? ' flow-map__point--highlight' : ' flow-map__point--dimmed') : '';
              const flag = p.flag ? ` flow-map__point--${p.flag}` : '';
              return (
                <span key={p.id} className={`flow-map__point${flag}${emphasis}`} data-testid="flow-map-point" style={{ left: pct(p.x), top: pct(p.y) }}>
                  <span className="flow-map__point-label">{pointLabel(p.sequence)}</span>
                  {formatRange(p.levelMin, p.levelMax)}
                  {p.alternating && '~'}
                </span>
              );
            })}
          {showMeasurements &&
            corals.map((c) => (
              <span key={c.id} className={`flow-map__coral flow-map__coral--${c.status}`} data-testid="flow-map-coral" style={{ left: pct(c.x), top: pct(c.y) }}>
                {c.number}
                {CORAL_GLYPH[c.status]}
              </span>
            ))}
          {pending && <span className="par-map__pending" data-testid="flow-map-pending" style={{ left: pct(pending.x), top: pct(pending.y) }} />}
        </div>
      </div>
    </figure>
  );
}

/** Legend of the 0–4 flow scale: swatch + level in words; "~" = direction changes; the facings when a pump has one. */
export function FlowMapLegend({ facings = [] }: { facings?: readonly ('front' | 'back')[] }) {
  const { t } = useTranslation();
  return (
    <ul className="par-map__legend" aria-label={t('flow.map.legendLabel')}>
      {FLOW_LEVELS.map((level) => (
        <li key={level} className="flow-map__legend-item">
          <span className={`flow-map__swatch flow-map__cell--${level}`} aria-hidden="true" />
          <span>{t('flow.levels.short', { level, name: t(`flow.levels.${level}.name`) })}</span>
        </li>
      ))}
      <li className="flow-map__legend-item">
        <span aria-hidden="true">~</span>
        <span>{t('flow.map.alternatingLegend')}</span>
      </li>
      {facings.map((f) => (
        <li key={f} className="flow-map__legend-item">
          <span aria-hidden="true">{FACING_GLYPH[f]}</span>
          <span>{t(`flow.map.facingLegend.${f}`)}</span>
        </li>
      ))}
    </ul>
  );
}

export default FlowMapPhoto;
